import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Alert, Box, CircularProgress, Divider, InputAdornment, TextField, Typography } from '@mui/material';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { useMutation } from 'react-query';
import rootApi from '@utils/baseAPI/WrapperApi';
import { showError, showSuccess } from '@components/notification';
import { addCommas } from '@utils/urls';

const DialogRefund = ({ customer, open, setOpen, refetch }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [amountError, setAmountError] = useState('');

  const currentBalance = customer?.wallet_response?.amount ?? 0;

  const handleClose = () => {
    setOpen(false);
    setAmount('');
    setNote('');
    setAmountError('');
  };

  const validateAmount = (val) => {
    const num = Number(val);
    if (!val || isNaN(num) || num <= 0) {
      setAmountError('Vui lòng nhập số tiền hợp lệ (> 0)');
      return false;
    }
    if (num > currentBalance) {
      setAmountError(`Vượt quá số dư hiện tại (${addCommas(currentBalance)} đ)`);
      return false;
    }
    setAmountError('');
    return true;
  };

  const { mutateAsync, isLoading } = useMutation(['refund-user', customer?.id], () =>
    rootApi.post(`/admin/users/${customer?.id}/refund`, {
      amount: Number(amount),
      note: note.trim()
    })
  );

  const handleConfirm = () => {
    if (!validateAmount(amount)) return;
    if (!note.trim()) {
      showError('Vui lòng nhập lý do và thông tin tài khoản ngân hàng!');
      return;
    }
    mutateAsync()
      .then(() => {
        showSuccess({ message: `Đã trừ ${addCommas(Number(amount))} đ từ tài khoản ${customer?.full_name || customer?.username}!` });
        handleClose();
        refetch();
      })
      .catch((e) => {
        const msg = e?.response?.data?.message;
        showError(msg || 'Trừ tiền thất bại! Vui lòng kiểm tra lại số dư.');
      });
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MoneyOffIcon sx={{ color: '#DC2626' }} />
        Trừ tiền / Hoàn tiền khách hàng
      </DialogTitle>

      <DialogContent>
        {/* Thông tin khách hàng */}
        <Box
          sx={{
            bgcolor: '#F4F4F5',
            borderRadius: 1,
            padding: '12px 16px',
            marginBottom: 2
          }}>
          <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
            Thông tin khách hàng
          </Typography>
          <Typography variant="body2">
            Tên: <strong>{customer?.full_name || '—'}</strong>
          </Typography>
          <Typography variant="body2">
            Tên đăng nhập: <strong>{customer?.username}</strong>
          </Typography>
          <Typography variant="body2">
            Số điện thoại: <strong>{customer?.phone_number || '—'}</strong>
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2">
            Số dư hiện tại:{' '}
            <strong style={{ color: '#2D9764', fontSize: '15px' }}>
              {addCommas(currentBalance)} đ
            </strong>
          </Typography>
        </Box>

        {/* Số tiền */}
        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
            Số tiền cần trừ <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="Nhập số tiền..."
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              if (amountError) validateAmount(e.target.value);
            }}
            error={!!amountError}
            helperText={amountError}
            InputProps={{
              endAdornment: <InputAdornment position="end">đ</InputAdornment>
            }}
          />
          {amount && !amountError && (
            <Typography variant="caption" sx={{ color: '#71717A' }}>
              = {addCommas(Number(amount))} đ &nbsp;|&nbsp; Số dư còn lại sau trừ:{' '}
              <strong>{addCommas(currentBalance - Number(amount))} đ</strong>
            </Typography>
          )}
        </Box>

        {/* Lý do + thông tin tài khoản NH */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
            Lý do trừ tiền & thông tin tài khoản ngân hàng nhận{' '}
            <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={5}
            size="small"
            placeholder={`Nhập lý do trừ tiền và thông tin tài khoản ngân hàng để chuyển trả...\n\nVí dụ:\nLý do: Khách hàng không còn nhu cầu sử dụng dịch vụ sạc.\nTài khoản nhận: Vietcombank - 1234567890 - Nguyễn Văn A`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Typography variant="caption" sx={{ color: '#71717A' }}>
            Thông tin này sẽ được lưu vào lịch sử giao dịch.
          </Typography>
        </Box>

        {amount && !amountError && Number(amount) > 0 && (
          <Alert severity="warning" sx={{ marginTop: 2 }}>
            Thao tác này sẽ trừ <strong>{addCommas(Number(amount))} đ</strong> khỏi ví của{' '}
            <strong>{customer?.full_name || customer?.username}</strong> và không thể hoàn tác.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '8px 24px 16px' }}>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading || !amount || !note.trim() || !!amountError}
          sx={{
            bgcolor: '#DC2626',
            '&:hover': { bgcolor: '#B91C1C' },
            '&.Mui-disabled': { bgcolor: '#FCA5A5', color: '#FFF' }
          }}>
          {isLoading && <CircularProgress size={14} sx={{ mr: 1, color: '#FFF' }} />}
          Xác nhận trừ tiền
        </Button>
        <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
          Hủy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogRefund;
